import { BodyPrimary, BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { CaretRight } from '@liveauctioneers/hammer-ui-core/icons/v2';
import { Form } from '@liveauctioneers/caterwaul-components/lib/types/Form';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import {
    isValidValidationObject,
    validateValidationObject,
    validateValue,
} from '@liveauctioneers/caterwaul-components/lib/utils/validation';
import { Range } from '@liveauctioneers/caterwaul-components/lib/Facets/RangeFacet/index';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Validation } from '@liveauctioneers/caterwaul-components/lib/types/Validation';
import Facet from './Facet';
import Input from '@liveauctioneers/caterwaul-components/lib/Input/Input';
import isEqual from 'react-fast-compare';
import React, { ChangeEvent, KeyboardEvent, useCallback, useState } from 'react';
import styled from 'styled-components';
import useForm from '@liveauctioneers/caterwaul-components/lib/hooks/useForm';

type Props = {
    facetId: string;
    loadPageWithFacetCollapsed: boolean;
    range: Form<Range>;
    rangeEntered: Function;
    title: string;
};

const defaultValidation: Validation<Form<Range>> = {
    max: {
        validate: (value, form) =>
            validateValue(value, { maxLength: { value: 10 }, number: true }, (value) => {
                const newMax = Number(value);
                if (newMax < Number(form.min) && newMax > 0) {
                    return 'Max must be greater than min';
                }
            }),
    },
    min: {
        validate: (value, form) =>
            validateValue(value, { maxLength: { value: 10 } }, (value) => {
                const newMin = Number(value);
                const max = Number(form.max);
                if (max <= newMin && max > 0) {
                    return 'Max must be greater than min';
                }
            }),
    },
};

const RangeFacet = ({ facetId, loadPageWithFacetCollapsed, range, rangeEntered, title }: Props) => {
    const { formatMessage } = useIntl();

    const [valid, setValid] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [minValue, setMinValue] = useState<number>(null);
    const [maxValue, setMaxValue] = useState<number>(null);

    const onChange = useCallback((form: Form<Range>, validation: Validation<Form<Range>>) => {
        setValid(isValidValidationObject(validateValidationObject(form, validation)));
    }, []);

    const { form, handleFormChange, validity } = useForm(range, defaultValidation, onChange);

    const rangeClickHandler = useCallback(() => {
        setSubmitted(true);
        if (valid) {
            rangeEntered(facetId, form.min, form.max);
            setSubmitted(false);
        }
    }, [valid, rangeEntered, facetId, form.min, form.max]);

    const hasAtLeastOneFieldFilled =
        (minValue !== null && !Number.isNaN(minValue)) || (maxValue !== null && !Number.isNaN(maxValue));

    const onValueChange = (event: ChangeEvent<HTMLInputElement>, isMax: boolean) => {
        const { value } = event.target;

        if (value !== null && value !== undefined && (!value.match(/\D/g) || value.length === 0)) {
            const numericalValue = Number(value);
            const newValue = numericalValue > 0 || value === '0' ? numericalValue : null;
            if (isMax) {
                setMaxValue(newValue);
            } else {
                setMinValue(newValue);
            }
            handleFormChange(event);
        }
    };

    const onKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
        blockInvalidChar(event);
        if (event.key === 'Enter' && hasAtLeastOneFieldFilled) {
            rangeClickHandler();
        }
    };

    const blockInvalidChar = (event) => ['e', 'E', '+', '-', '.'].includes(event.key) && event.preventDefault();

    return (
        <Facet
            closeFacet={loadPageWithFacetCollapsed}
            title={title}
        >
            <StyledForm>
                <StyledRangeInput
                    aria-label={formatMessage({ id: 'search_minimum' })}
                    hideSpacer
                    iconLeft={<BodyPrimary color="info">$</BodyPrimary>}
                    iconLeftPadding={20}
                    id={`${title} min`}
                    name="min"
                    onChange={(event) => onValueChange(event as ChangeEvent<HTMLInputElement>, false)}
                    onKeyPress={onKeyPress}
                    placeholder={formatMessage({ id: 'search_minimum' })}
                    small
                    type="number"
                    valid={!submitted || !validity.min.errorMessage}
                    value={minValue || ''}
                />
                <StyledRangeSeperator>&#45;</StyledRangeSeperator>
                <StyledRangeInput
                    aria-label={formatMessage({ id: 'search_maximum' })}
                    hideSpacer
                    iconLeft={<BodyPrimary color="info">$</BodyPrimary>}
                    iconLeftPadding={20}
                    id={`${title} max`}
                    name="max"
                    onChange={(event) => onValueChange(event as ChangeEvent<HTMLInputElement>, true)}
                    onKeyPress={onKeyPress}
                    placeholder={formatMessage({ id: 'search_maximum' })}
                    small
                    type="number"
                    valid={!submitted || !validity.max.errorMessage}
                    value={maxValue || ''}
                />
                <IconButton
                    aria-label="Submit range"
                    className="ml-2xs"
                    disabled={!hasAtLeastOneFieldFilled}
                    icon={CaretRight}
                    onClick={rangeClickHandler}
                    shape="square"
                    size="sm"
                    style="primary"
                    type="submit"
                    variant="filled"
                />
                <BodySecondary
                    className="mb-[-10px] w-full"
                    color="critical"
                >
                    {submitted && (validity.min.errorMessage || validity.max.errorMessage)}
                </BodySecondary>
            </StyledForm>
        </Facet>
    );
};

const StyledForm = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const StyledRangeInput = styled(Input)`
    max-width: 39%;

    // firefox
    input {
        /* stylelint-disable-next-line property-no-vendor-prefix */
        -moz-appearance: textfield;
    }

    // everything else
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        /* stylelint-disable-next-line property-no-vendor-prefix */
        -webkit-appearance: none;
        margin: 0;
    }
`;

const StyledRangeSeperator = styled.span`
    color: ${({ theme }) => theme.colors.grey100};
    padding: 3px 5px 0;
`;

export default React.memo<Props>(RangeFacet, isEqual);
